import React, { useMemo, useState } from 'react';
import { CalendarClock, Check, X } from 'lucide-react';
import { Modal } from '../reusable';
import ProductionStatusChip from './ProductionStatusChip';
import { formatRequestQty } from './commercialRequestsUtils';
import {
  formatContainerBreakdown,
  formatDateTimeEs,
  formatDeadline,
  getActiveItemProgressOptions,
  getRequestStatusColor,
  ITEM_PROGRESS,
  REQUEST_STATUS,
} from './planningDomain';
import { FORMULA_KIND_BASE } from './productionDomain';
import { useCommercialCatalogRefresh } from '../../hooks/useCommercialCatalogRefresh';

const Info = ({ label, value, muted, text }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: text, wordBreak: 'break-word', lineHeight: 1.35 }}>
      {value || '—'}
    </div>
  </div>
);

const canEditItemProgress = (requestStatus) =>
  [REQUEST_STATUS.planned, REQUEST_STATUS.inProcess, REQUEST_STATUS.finished].includes(requestStatus);

const isItemFinished = (item) =>
  String(item?.progress_status || '').trim() === ITEM_PROGRESS.finished || Boolean(item?.finished_at);

/** Ficha de solicitud de producto con listado de ítems y avance por producto. */
const ProductRequestViewModal = ({
  isOpen,
  onClose,
  request,
  items = [],
  isDarkMode = false,
  canAccept = false,
  canReject = false,
  canReschedule = false,
  onAccept,
  onReject,
  onReschedule,
  onItemProgressChange,
}) => {
  const catalogTick = useCommercialCatalogRefresh();
  const progressOptions = useMemo(() => getActiveItemProgressOptions(), [catalogTick]);
  const [savingItemId, setSavingItemId] = useState(null);

  if (!request) return null;
  const text = isDarkMode ? '#f4f4f5' : '#111827';
  const muted = isDarkMode ? '#a1a1aa' : '#6b7280';
  const border = isDarkMode ? '#3f3f46' : '#e5e7eb';
  const inputBg = isDarkMode ? '#18181b' : '#ffffff';
  const editableProgress = canEditItemProgress(request.status) && typeof onItemProgressChange === 'function';
  const defaultProgress = progressOptions[0] || 'Planificado';
  const requestFinishedAt = formatDateTimeEs(request.finished_at);
  const panel = {
    border: `1px solid ${border}`,
    borderRadius: 10,
    padding: 12,
    background: isDarkMode ? '#18181b' : '#f9fafb',
  };
  const footerBtn = (bg) => ({
    height: 34,
    padding: '0 12px',
    borderRadius: 8,
    border: bg ? 'none' : `1px solid ${isDarkMode ? '#3f3f46' : '#d1d5db'}`,
    background: bg || (isDarkMode ? '#18181b' : '#ffffff'),
    color: bg ? '#ffffff' : text,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  });
  const th = (align = 'left') => ({
    textAlign: align,
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: muted,
    borderBottom: `1px solid ${border}`,
    whiteSpace: 'nowrap',
  });
  const td = (align = 'left') => ({
    textAlign: align,
    padding: '8px 10px',
    fontSize: 13,
    color: text,
    borderTop: `1px solid ${border}`,
  });

  const handleProgressChange = async (item, nextStatus) => {
    const itemId = item.item_id || item.id;
    if (!itemId || !editableProgress || isItemFinished(item)) return;
    const current = String(item.progress_status || defaultProgress).trim();
    if (current === nextStatus) return;
    setSavingItemId(itemId);
    try {
      await onItemProgressChange?.(item, nextStatus);
    } finally {
      setSavingItemId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDarkMode={isDarkMode}
      size="xl"
      title={`Solicitud ${request.code || ''}`.trim()}
      subtitle={[request.sale_code, request.organization_name].filter(Boolean).join(' · ') || request.origin}
      headerActions={
        <ProductionStatusChip status={request.status} color={getRequestStatusColor(request.status)} />
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
          <button type="button" onClick={onClose} style={footerBtn()}>
            Cerrar
          </button>
          {canReschedule ? (
            <button type="button" onClick={() => onReschedule?.(request)} style={footerBtn('#64748b')}>
              <CalendarClock size={14} /> Reprogramar
            </button>
          ) : null}
          {canReject ? (
            <button type="button" onClick={() => onReject?.(request)} style={footerBtn('#dc2626')}>
              <X size={14} /> Cancelar
            </button>
          ) : null}
          {canAccept ? (
            <button type="button" onClick={() => onAccept?.(request)} style={footerBtn('#16a34a')}>
              <Check size={14} /> Aceptar
            </button>
          ) : null}
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 14, minWidth: 0 }}>
        <section style={panel}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: text }}>Resumen</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px 16px' }}>
            <Info label="Venta" value={request.sale_code} muted={muted} text={text} />
            <Info label="Cotización" value={request.quotation_code} muted={muted} text={text} />
            <Info label="Cliente" value={request.organization_name} muted={muted} text={text} />
            <Info label="Solicitado por" value={request.executive || request.created_by} muted={muted} text={text} />
            <Info
              label="Fecha límite"
              value={formatDeadline(request.needed_by, request.needed_by_time)}
              muted={muted}
              text={text}
            />
            <Info label="OP vinculada" value={request.op_code} muted={muted} text={text} />
            <Info label="Productos" value={String(request.items_count ?? items.length)} muted={muted} text={text} />
            <Info
              label="¼ gal / gal / T16 / T20"
              value={formatContainerBreakdown(request)}
              muted={muted}
              text={text}
            />
            <Info label="Origen" value={request.origin} muted={muted} text={text} />
            {requestFinishedAt ? (
              <Info label="Terminada el" value={requestFinishedAt} muted={muted} text={text} />
            ) : null}
          </div>
          {request.rejection_reason ? (
            <div style={{ marginTop: 12 }}>
              <Info
                label={
                  request.status === REQUEST_STATUS.cancelled
                    ? 'Motivo de la cancelación'
                    : 'Motivo del rechazo'
                }
                value={request.rejection_reason}
                muted={muted}
                text="#dc2626"
              />
            </div>
          ) : null}
          {request.notes ? (
            <div style={{ marginTop: 12 }}>
              <Info label="Notas" value={request.notes} muted={muted} text={text} />
            </div>
          ) : null}
        </section>

        <section style={panel}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: text }}>
            Productos solicitados ({items.length})
          </h4>
          <div style={{ overflowX: 'auto', border: `1px solid ${border}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr style={{ background: isDarkMode ? '#0f0f10' : '#ffffff' }}>
                  <th style={th()}>Producto</th>
                  <th style={th()}>Color</th>
                  <th style={th('right')}>Cantidad</th>
                  <th style={th()}>Avance</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...td('center'), color: muted, padding: 18 }}>
                      Sin productos registrados.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const itemId = item.item_id || item.id;
                    const progress =
                      String(item.progress_status || '').trim() || defaultProgress;
                    const finished = isItemFinished(item);
                    const finishedLabel = formatDateTimeEs(item.finished_at);
                    const canEditThisItem = editableProgress && !finished;
                    return (
                      <tr key={itemId}>
                        <td style={td()}>
                          <div style={{ fontWeight: 600 }}>{item.product_name || '—'}</div>
                          <div style={{ fontSize: 11, color: muted }}>{item.product_code || ''}</div>
                        </td>
                        <td style={td()}>
                          {[item.color_code, item.color_name].filter(Boolean).join(' · ') ||
                            (item.formula_kind === FORMULA_KIND_BASE ? 'Base' : '—')}
                        </td>
                        <td style={{ ...td('right'), fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {formatRequestQty(item.quantity, item.unit)}
                        </td>
                        <td style={td()}>
                          {canEditThisItem ? (
                            <select
                              value={progress}
                              disabled={savingItemId === itemId}
                              onChange={(event) => handleProgressChange(item, event.target.value)}
                              style={{
                                height: 32,
                                minWidth: 140,
                                borderRadius: 8,
                                border: `1px solid ${border}`,
                                background: inputBg,
                                color: text,
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '0 8px',
                              }}
                            >
                              {progressOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: muted }}>{progress}</div>
                              {finishedLabel ? (
                                <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                                  {finishedLabel}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ProductRequestViewModal;
